import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AISystemsService } from 'services/AISystemService';
import { RiskRegisterService } from 'services/RiskRegisterService';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import { Utils } from 'shared/helpers';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

interface ILinkedAISystems {
  linkedSystems: IAISystem[];
  riskId: string;
  reloadDetail: () => void;
}

const LinkedAISystems: React.FC<ILinkedAISystems> = (props) => {
  const { linkedSystems, riskId, reloadDetail } = props;
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IAISystem[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [isLinking, setIsLinking] = useState('');
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const theme = useTheme();

  // API Calls
  const loadAISystems = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();
    AISystemsService.getAISystems(queryString)
      .then((response) => {
        const { aiSystems } = response.data.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(aiSystems.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const linkRecord = (payload: any, callback: any): void => {
    RiskRegisterService.linkAISystem(riskId, payload)
      .then((response) => {
        callback(response);
      })
      .catch((e) => {
        Utils.handleErrorResponse(e);
        callback(false);
      });
  };

  // UI Actions
  const actionOnLink = (status: string, id: string): void => {
    setIsLinking(status);
    linkRecord(
      {
        aisystemId: id,
        action: status,
      },
      (response: any) => {
        setIsLinking('');
        setSelectedRecordId('');
        if (typeof response === 'object') {
          reloadDetail();
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    loadAISystems();
  }, []);

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Box className="title">
        <Typography variant="h4">Linked AI Systems</Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            <Stack
              direction="row"
              spacing={4}
              alignItems="center"
              sx={{
                [theme.breakpoints.up('lg')]: {
                  width: '50%',
                },
              }}
            >
              <AppSelectWithKeys
                className
                label=""
                onChange={(e: any) => {
                  setSelectedRecordId(e.target.value);
                }}
                items={records.filter(
                  (r) => !linkedSystems.some((ls) => ls._id === r._id)
                )}
                titleKey="solutionName"
                valueKey="_id"
              />
              {orgACLConfig.riskManagement?.includes(
                'riskManagement.riskRegister.view.linkedAISystems.link'
              ) && (
                <AppPrimaryButtonOutlined
                  isLoading={isLinking === 'link'}
                  disabled={!selectedRecordId}
                  title="Link"
                  onClick={() => {
                    actionOnLink('link', selectedRecordId);
                  }}
                />
              )}
            </Stack>
            {linkedSystems.map((aiSystem, index) => (
              <List
                style={{
                  border: '1px solid lightgray',
                  marginTop: '10px',
                  borderRadius: '5px',
                }}
              >
                <ListItem>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: '100%' }}
                  >
                    <Stack>
                      <Typography className="f18 fw-6 mb-2">
                        {aiSystem.solutionName}
                      </Typography>
                      <Typography variant="body1">
                        {aiSystem.organizationUnit}
                      </Typography>
                    </Stack>
                    {orgACLConfig.riskManagement?.includes(
                      'riskManagement.riskRegister.view.linkedAISystems.unlink'
                    ) && (
                      <AppPrimaryButton
                        isLoading={selectedRecordId === aiSystem._id}
                        onClick={() => {
                          setSelectedRecordId(aiSystem._id);
                          actionOnLink('unlink', aiSystem._id);
                        }}
                        className="btn-danger btn-small"
                        title="Unlink"
                      />
                    )}
                  </Stack>
                </ListItem>
                {index < linkedSystems.length - 1 && <Divider />}
              </List>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LinkedAISystems;
